import React, { useState } from 'react';
import { Image as ImageIcon, X } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

const LogoSettings = ({ generalSettings, setGeneralSettings }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [logoPreview, setLogoPreview] = useState(generalSettings.logo);

  const updateFaviconPreview = (logoUrl) => {
    const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
    existingFavicons.forEach(favicon => favicon.remove());
    
    if (logoUrl && logoUrl !== 'null' && logoUrl !== '') {
      const favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.type = 'image/x-icon';
      favicon.href = logoUrl;
      document.head.appendChild(favicon);
      
      const appleFavicon = document.createElement('link');
      appleFavicon.rel = 'apple-touch-icon';
      appleFavicon.href = logoUrl;
      document.head.appendChild(appleFavicon);
      
      console.log('🎨 Favicon preview updated with uploaded logo');
    } else {
      const svgFavicon = `data:image/svg+xml,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
          <rect x="0" y="0" width="24" height="24" fill="%23059669" rx="4"/>
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          <polyline points="3.27,6.96 12,12.01 20.73,6.96"/>
          <line x1="12" y1="22.08" x2="12" y2="12"/>
        </svg>
      `)}`;
      
      const favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.type = 'image/svg+xml';
      favicon.href = svgFavicon;
      document.head.appendChild(favicon);
      
      console.log('🎨 Favicon preview updated with default package icon');
    }
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        throw new Error(t('admin.logo.maxSize'));
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setGeneralSettings({...generalSettings, logo: reader.result});
        updateFaviconPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setGeneralSettings({...generalSettings, logo: null});
    updateFaviconPreview(null);
  };

  return (
    <div className="relative group mb-6">
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20'
        : 'glass-light-card border border-slate-200'
      } ${isDarkMode ? 'backdrop-blur-2xl' : ''} rounded-3xl ${isDarkMode ? 'shadow-[0_8px_32px_rgba(0,0,0,0.4)]' : 'shadow-sm'}`}></div>
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
        : ''
      } rounded-3xl`}></div>
      
      <div className="relative p-6">
        <h4 className={`text-lg font-semibold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'} mb-6`}>{t('admin.logo.title')}</h4>
        <div className="space-y-6">
          {logoPreview ? (
            <div className="relative inline-block group/logo">
              <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 backdrop-blur-xl border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.3)]' : 'bg-slate-50 border border-slate-200 shadow-sm'} rounded-2xl`}></div>
              <div className="relative p-4 rounded-2xl">
                <img 
                  src={logoPreview} 
                  alt="Portal Logo" 
                  className="max-h-32 max-w-full rounded-xl drop-shadow-lg"
                />
              </div>
              
              <button
                onClick={removeLogo}
                className="absolute -top-2 -right-2 group/remove transition-all duration-300 transform hover:scale-[1.1] active:scale-[0.9]"
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(220,38,38,0.7) 0%, rgba(185,28,28,0.7) 50%, rgba(153,27,27,0.7) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  boxShadow: '0 4px 16px rgba(239,68,68,0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
                  borderRadius: '50%'
                } : {
                  background: 'rgb(220,38,38)',
                  borderRadius: '50%'
                }}
              >
                <div
                  className={`absolute inset-0 rounded-full transition-all duration-300 opacity-0 group-hover/remove:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                  style={isDarkMode ? {
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                  } : {}}
                ></div>
                <div className="relative w-8 h-8 flex items-center justify-center rounded-full">
                  <X className="w-4 h-4 text-white transition-transform duration-300 group-hover/remove:scale-110 drop-shadow-lg" />
                  <div className="absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm"></div>
                </div>
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center w-full">
              <label className="relative group/upload cursor-pointer">
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 backdrop-blur-xl border-white/30 shadow-[0_8px_32px_rgba(0,0,0,0.3)]' : 'bg-white border-slate-300 shadow-sm'} rounded-3xl border-2 border-dashed`}></div>
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10' : 'bg-brand/5'} rounded-3xl opacity-0 group-hover/upload:opacity-100 transition-opacity duration-300`}></div>
                
                <div className="relative w-64 flex flex-col items-center px-6 py-8 rounded-3xl transition-all duration-300 group-hover/upload:scale-105">
                  <div className="relative group/icon mb-4">
                    <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent blur-sm' : 'hidden'} rounded-2xl`}></div>
                    <div className={`relative ${isDarkMode ? 'bg-gradient-to-br from-slate-600/60 via-gray-600/50 to-slate-700/60 backdrop-blur-xl border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.3)]' : 'bg-brand/10 border border-brand/20'} w-12 h-12 rounded-2xl flex items-center justify-center`}>
                      <ImageIcon className={`w-6 h-6 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-brand'}`} />
                      <div className={`absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                    </div>
                  </div>
                  <span className={`font-medium ${isDarkMode ? 'text-white/80 drop-shadow-sm' : 'text-slate-700'}`}>{t('admin.logo.select')}</span>
                  <input type="file" className="hidden" onChange={handleLogoChange} accept="image/*" />
                </div>
              </label>
            </div>
          )}
          <p className={`text-sm ${isDarkMode ? 'text-white/60 drop-shadow-sm' : 'text-slate-600'}`}>
            {t('admin.logo.formats')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LogoSettings;